import { type ReactNode, useEffect, useRef, useState } from 'react';

import { ErrorPopup } from './ErrorPopup';
import {
  type AppErrorContext,
  type AppErrorItem,
  normalizeAppError,
} from './errorSupport';

const COOLDOWN_MS = 60_000;
const MAX_QUEUE = 20;
let queue: AppErrorItem[] = [];
const dismissed = new Map<string, number>();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

export function reportAppError(
  error: unknown,
  context: AppErrorContext = {},
): void {
  const incoming = normalizeAppError(error, context);
  const dismissedAt = dismissed.get(incoming.fingerprint) ?? 0;
  if (Date.now() - dismissedAt < COOLDOWN_MS) return;
  const existing = queue.find(
    (item) => item.fingerprint === incoming.fingerprint,
  );
  if (existing) {
    existing.occurrences += 1;
    existing.lastOccurredAt = incoming.lastOccurredAt;
    existing.retry = incoming.retry ?? existing.retry;
  } else {
    queue = [...queue, incoming].slice(-MAX_QUEUE);
  }
  emit();
}

const dismissCurrent = () => {
  const current = queue[0];
  if (current) dismissed.set(current.fingerprint, Date.now());
  queue = queue.slice(1);
  emit();
};

export function resetErrorCenterForTests(): void {
  queue = [];
  dismissed.clear();
  emit();
}

export function ErrorPopupProvider({
  children,
  initialError,
}: {
  children: ReactNode;
  initialError?: unknown;
}) {
  const initialReported = useRef(false);
  const [, rerender] = useState(0);
  useEffect(() => {
    const listener = () => rerender((value) => value + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  useEffect(() => {
    if (!initialError || initialReported.current) return;
    initialReported.current = true;
    reportAppError(initialError, { endpoint: 'runtime-config', pathname: '/' });
  }, [initialError]);
  return (
    <>
      {children}
      <ErrorPopup item={queue[0] ?? null} onClose={dismissCurrent} />
    </>
  );
}
